/**
 * Turning a Theme into live CSS.
 *
 * Every value becomes a custom property on the document root, so switching
 * theme is one attribute write and no component re-render. Components read
 * `var(--ink)` and never a theme object, which is what keeps adding a theme to
 * a data change rather than a code change.
 */
import type { Theme } from "./tokens";
import { themeById, DEFAULT_THEME_ID } from "./themes";

// The keys keep the old product name. A new name would reset every saved theme.
const STORAGE_KEY = "halimail.theme";
const MIGRATION_KEY = "halimail.theme.migrated";
/**
 * The id that used to be the default.
 *
 * Every browser that opened the client before 2026-09-24 has this stored whether
 * or not anyone chose it, because applying a theme remembers it. Left alone,
 * the direction change would reach nobody who had already visited: they would
 * keep INDEX's flat bar and square corners and see none of Aura.
 */
const SUPERSEDED_DEFAULT = "index";
let loadedFonts: string | null = null;

/** Remembered choice, falling back to the art bible's direction. */
export function preferredThemeId(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!localStorage.getItem(MIGRATION_KEY)) {
      localStorage.setItem(MIGRATION_KEY, "1");
      if (stored === SUPERSEDED_DEFAULT) {
        localStorage.removeItem(STORAGE_KEY);
        return DEFAULT_THEME_ID;
      }
    }
    return stored ?? DEFAULT_THEME_ID;
  } catch {
    // Private windows and blocked site data both throw here; a theme
    // preference is never worth failing a page load over.
    return DEFAULT_THEME_ID;
  }
}

export function rememberTheme(id: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* preference is a convenience, not state the app depends on */
  }
}

function loadFonts(theme: Theme): void {
  const query = theme.type.googleFonts;
  if (!query || loadedFonts === query) return;
  const href = `https://fonts.googleapis.com/css2?family=${query}&display=swap`;
  let link = document.querySelector<HTMLLinkElement>("link[data-theme-fonts]");
  if (!link) {
    link = document.createElement("link");
    link.rel = "stylesheet";
    link.dataset["themeFonts"] = "";
    document.head.appendChild(link);
  }
  link.href = href;
  loadedFonts = query;
}

/** Write a theme's tokens onto the document. Idempotent. */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  const set = (name: string, value: string) => root.style.setProperty(name, value);

  for (const [role, value] of Object.entries(theme.colors)) {
    set(`--${kebab(role)}`, value);
  }
  set("--font-sans", theme.type.sans);
  set("--font-mono", theme.type.mono);
  set("--tracking", theme.type.tracking);
  set("--row-h", `${theme.density.row}px`);
  set("--size-primary", `${theme.density.primary}px`);
  set("--size-secondary", `${theme.density.secondary}px`);
  set("--size-meta", `${theme.density.meta}px`);
  set("--list-width", theme.density.listWidth ? `${theme.density.listWidth}px` : "100%");
  set("--rail-w", `${theme.density.rail}px`);
  set("--radius", `${theme.density.radius}px`);
  set("--line-height", String(theme.density.lineHeight));
  for (const [box, glyph] of Object.entries(theme.markers)) {
    set(`--marker-${kebab(box)}`, `"${glyph}"`);
  }

  // A theme without chrome still has to produce every token the stylesheet
  // reads, so the fallbacks come from its own roles: a flat bar, the Inbox hue
  // as the accent, and the raised surface as the pill.
  const chrome = theme.chrome ?? {
    from: theme.colors.ground,
    to: theme.colors.ground,
    accent: theme.colors.inbox,
    onAccent: theme.colors.surface,
    tag: theme.colors.raised,
    tagInk: theme.colors.dim,
  };
  for (const [role, value] of Object.entries(chrome)) {
    set(`--chrome-${kebab(role)}`, value);
  }

  root.dataset["theme"] = theme.id;
  root.style.colorScheme = theme.scheme;

  // Anything tokens cannot express, scoped to a single style element so
  // switching themes never leaves the previous one's rules behind.
  let extra = document.querySelector<HTMLStyleElement>("style[data-theme-extra]");
  if (!extra) {
    extra = document.createElement("style");
    extra.dataset["themeExtra"] = "";
    document.head.appendChild(extra);
  }
  extra.textContent = theme.extraCss ? `body { ${theme.extraCss} }` : "";

  loadFonts(theme);
}

/** Apply the remembered theme and return it. */
export function initTheme(): Theme {
  const theme = themeById(preferredThemeId());
  applyTheme(theme);
  return theme;
}

/** Anything that must redraw when the theme changes. One listener is enough. */
let listener: (() => void) | null = null;

/**
 * Register the redraw for a theme change.
 *
 * The picker lives in the app shell, and the shell needs to repaint without
 * building a new poller — whose first run syncs, for a cosmetic change. A
 * listener here means the picker does not have to call back into the app it was
 * handed, so no render is nested inside another.
 *
 * Returns a function that removes the listener.
 */
export function onThemeChange(fn: () => void): () => void {
  listener = fn;
  return () => {
    if (listener === fn) listener = null;
  };
}

export function selectTheme(id: string): Theme {
  const theme = themeById(id);
  applyTheme(theme);
  rememberTheme(theme.id);
  listener?.();
  return theme;
}

function kebab(s: string): string {
  return s.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
}
